/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useCurrentQueueItem } from "@packages/media";
import { forwardRef, useEffect } from "react";

import { numericPercentages, spacing } from "../../../../../global/emotion";
import type { ButtonProps } from "../../../../components/_base/_core/Button/Button.emotion";
import { Button } from "../../../../components/_base/_core/Button/Button.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";

const followAudioButtonStyle: Interpolation = {
  bottom: spacing.mediaPlayerWithoutMenuBarMobile,
  left: numericPercentages[50],
  position: "fixed",
  transform: "translateX(-50%)",
  [cssMediaQueryMinWidthTablet]: { bottom: spacing.mediaPlayerDesktop },
};

export type FollowAudioButtonProps = ButtonProps;

export const FollowAudioButton = forwardRef<
  HTMLButtonElement,
  FollowAudioButtonProps
>(({ onClick, className = "" }, ref) => {
  const analytics = useAnalytics();
  const { currentItem } = useCurrentQueueItem();
  const t = useTranslations();

  useEffect(() => {
    if (currentItem?.selected_audio?.prayer_id) {
      analytics.track({
        event: HallowAnalyticsEvent.ViewedFollowAudioButton,
        properties: {
          prayer: currentItem.selected_audio.prayer_id,
          screen_name: "transcript",
        },
      });
    }
  }, []);

  return (
    <Button
      ref={ref}
      css={followAudioButtonStyle}
      className={className}
      onClick={onClick}
      onPointerDown={() => {
        if (currentItem?.selected_audio?.prayer_id) {
          analytics.track({
            event: HallowAnalyticsEvent.TappedFollowAudio,
            properties: {
              prayer: currentItem.selected_audio.prayer_id,
              screen_name: "transcript",
            },
          });
        }
      }}
    >
      {t("tos_follow_audio")}
    </Button>
  );
});

FollowAudioButton.displayName = "FollowAudioButton";
